/**
 * coveredFee.ts — the one rule for sizing a donor-covered fee.
 *
 * When a donor ticks "cover fees", the fee is either:
 *   - GROSS-UP: (amount + fixed) / (1 - percent) - amount — the nonprofit nets the full gift;
 *   - FLAT:     amount * percent + fixed.
 *
 * Rule (owner ruling, Akif 2026-10-14, engine 1.10.0): a covered fee is grossed up iff
 *   (a) the org pays NO CharityStack platform fee — its resolved platform percent AND
 *       platform fixed fee are both 0; OR
 *   (b) its fee-deal billing plan is CUSTOM and it is NOT a catalog-priced tier
 *       (BASE/PLUS/PRO) — the pre-PE-2235 rule, so a CUSTOM org that pays a platform
 *       fee keeps exactly its old behaviour.
 * Everything else is flat. A donor who does not cover always gets the flat formula.
 * Clause (b) needs an explicit `isCatalogPricedTier: false`; an unknown flag reads as
 * catalog-priced (flat, the smaller donor charge).
 *
 * The checkout DISPLAYS this fee and the backends CHARGE it; the checkout sends no
 * fee on card/bank/wallet requests, the backend recomputes it. So this must agree
 * with the Python twin (python/charitystack_entitlements/covered_fee.py) to the
 * cent — the `expected_covered_fee` cases in fixtures/golden.json run through both.
 *
 * Self-contained on purpose (no catalog import): donor apps import it through the
 * `./covered-fee` subpath, and the donor bundle is byte-gated.
 */

const isZero = (value: unknown): boolean => {
  // Unknown (null / undefined / non-numeric / boolean) is NOT zero: an org whose
  // platform fee we cannot read is treated as fee-paying, i.e. flat.
  if (value === null || value === undefined || typeof value === 'boolean') return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  const n = Number(value);
  return Number.isFinite(n) && n === 0;
};

const isUncataloguedCustom = (billingPlan: unknown, isCatalogPricedTier: unknown): boolean =>
  typeof billingPlan === 'string' &&
  billingPlan.trim().toUpperCase() === 'CUSTOM' &&
  isCatalogPricedTier === false;

export const shouldGrossUpCoveredFee = (
  platformPercent: unknown,
  platformFixed: unknown,
  billingPlan: unknown = undefined,
  isCatalogPricedTier: unknown = undefined,
): boolean =>
  (isZero(platformPercent) && isZero(platformFixed)) ||
  isUncataloguedCustom(billingPlan, isCatalogPricedTier);

// A decimal as an integer scaled by 10^-scale: { units: 225n, scale: 3 } is 0.225.
interface Scaled {
  units: bigint;
  scale: number;
}

// Fractional digits kept for a gross-up quotient (a flat fee is exact). Python keeps
// 40 significant digits; both sit far below a double's precision, so both land on
// the same double.
const QUOTIENT_SCALE = 40;

const pow10 = (n: number): bigint => 10n ** BigInt(n);

/** A finite number on its SHORTEST decimal form (String(x), like Python's repr). */
const toScaled = (value: number): Scaled => {
  const [mantissa = '0', exponentPart] = String(Math.abs(value)).split('e');
  const exponent = exponentPart ? Number(exponentPart) : 0;
  const [intDigits = '0', fracDigits = ''] = mantissa.split('.');
  let units = BigInt(intDigits + fracDigits);
  let scale = fracDigits.length - exponent;
  if (scale < 0) {
    units *= pow10(-scale);
    scale = 0;
  }
  return { units: value < 0 ? -units : units, scale };
};

const rescale = (x: Scaled, scale: number): bigint => x.units * pow10(scale - x.scale);

const toNumber = ({ units, scale }: Scaled): number => {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const text = scale === 0 ? digits : `${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
  return Number(negative ? `-${text}` : text);
};

/**
 * Unrounded fee, computed in exact decimal arithmetic on the inputs' shortest
 * decimal forms, like the Python twin. Float math lost the half cent: $2.50 x 9% is
 * 0.225 exactly, but the float product is 0.22499999999999998 and rounded to $0.22
 * while the exact rule gives $0.23. Returns the double nearest the exact result,
 * which roundCurrency then reads on its shortest form.
 */
export const coveredFeeAmount = (
  amount: number,
  percent: number,
  fixed: number,
  grossUp: boolean,
): number => {
  if (grossUp && percent >= 1) {
    throw new RangeError(`percent must be < 1 for gross-up math, got ${percent}`);
  }
  const a = Number(amount);
  const p = Number(percent);
  const f = Number(fixed);
  // Non-finite inputs (undefined during a first render) keep the old NaN result;
  // roundCurrency passes it through.
  if (!Number.isFinite(a) || !Number.isFinite(p) || !Number.isFinite(f)) {
    return grossUp ? (a + f) / (1 - p) - a : a * p + f;
  }
  const A = toScaled(a);
  const P = toScaled(p);
  const F = toScaled(f);
  if (grossUp) {
    // (a + f) / (1 - p) - a, with the quotient carried to QUOTIENT_SCALE digits.
    const sumScale = Math.max(A.scale, F.scale);
    const numerator = rescale(A, sumScale) + rescale(F, sumScale);
    const denominator = pow10(P.scale) - P.units; // (1 - p) * 10^P.scale, > 0
    const quotient = (numerator * pow10(P.scale + QUOTIENT_SCALE)) / (denominator * pow10(sumScale));
    return toNumber({ units: quotient - rescale(A, QUOTIENT_SCALE), scale: QUOTIENT_SCALE });
  }
  // a * p + f, exactly.
  const product: Scaled = { units: A.units * P.units, scale: A.scale + P.scale };
  const scale = Math.max(product.scale, F.scale);
  return toNumber({ units: rescale(product, scale) + rescale(F, scale), scale });
};

/**
 * Half-up to the cent on the SHORTEST decimal form of the number — Python's
 * Decimal(repr(x)).quantize(0.01, ROUND_HALF_UP), which every backend charge path
 * applies. NOT toFixed(2): toFixed rounds the binary value, so 0.735 (stored as
 * 0.73499…) becomes 0.73 while the backend charges 0.74.
 */
export const roundCurrency = (value: number): number => {
  // Non-finite passes through (NaN in → NaN out), as the checkout's old toFixed math
  // did: fee inputs are briefly undefined during the first render, and a throw here
  // would take down the donor's checkout instead of showing a placeholder.
  if (!Number.isFinite(value)) return value;
  const negative = value < 0;
  // String(x) is the shortest round-trip form, like Python's repr; normalize any exponent.
  const [mantissa = '0', exponentPart] = String(Math.abs(value)).split('e');
  const exponent = exponentPart ? Number(exponentPart) : 0;
  let [intDigits = '0', fracDigits = ''] = mantissa.split('.');
  let digits = intDigits + fracDigits;
  let pointAt = intDigits.length + exponent;
  if (pointAt <= 0) {
    digits = '0'.repeat(1 - pointAt) + digits;
    pointAt = 1;
  } else if (pointAt > digits.length) {
    digits = digits + '0'.repeat(pointAt - digits.length);
  }
  intDigits = digits.slice(0, pointAt);
  fracDigits = digits.slice(pointAt).padEnd(3, '0');
  let cents = BigInt(intDigits + fracDigits.slice(0, 2));
  if (Number(fracDigits[2]) >= 5) cents += 1n;
  const result = Number(cents) / 100;
  return negative && result !== 0 ? -result : result;
};

export interface CoveredFeeInput {
  amount: number;
  percent: number;
  fixed: number;
  platformPercent: unknown;
  platformFixed: unknown;
  coverFees: boolean;
  /** The resolver's fee-deal billing plan (STARTER | CUSTOM). */
  billingPlan?: unknown;
  /** The resolver's isCatalogPricedTier (true for BASE/PLUS/PRO). */
  isCatalogPricedTier?: unknown;
}

/** The fee in dollars, rounded to the cent, exactly as displayed and charged. */
export const coveredFee = ({
  amount,
  percent,
  fixed,
  platformPercent,
  platformFixed,
  coverFees,
  billingPlan,
  isCatalogPricedTier,
}: CoveredFeeInput): number => {
  const grossUp =
    Boolean(coverFees) &&
    shouldGrossUpCoveredFee(platformPercent, platformFixed, billingPlan, isCatalogPricedTier);
  return roundCurrency(coveredFeeAmount(amount, percent, fixed, grossUp));
};
